import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { useParams } from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { ICategoryData } from "../../../interfaces/ICategoryData";
import { t } from "i18next";
import { IDepartmentData } from "../../../interfaces/IDepartmentData";
import { IPageProps } from "../../../interfaces/page-data";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";



const UpdateDepartment: React.FunctionComponent<IPageProps> = () => {
  const toast = useRef<any>(null);
  const { departmentId } = useParams<any>();
  const [departmentData, setDepartmentData] = useState<IDepartmentData>();
  const [companyData, setCompanyData] = useState<ICompanyData[]>();
  const [form] = Form.useForm();
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();

  useEffect(() => {
    fetchCompanyData();
  }, []);

  useEffect(() => {
    if (departmentId != null) {
      getDepartmentData(departmentId);
    } else if (departmentData?.departmentId) {
      getDepartmentData(departmentData.departmentId);
    }
  }, [departmentId, departmentData?.departmentId]);


  const fetchCompanyData = async () => {
    try {
      const result = await HTTPService.GETCompanyNames();
      setCompanyData(result);
    } catch (error) {
      console.error('Error fetching company data:', error);
      toast.current?.show({
        severity: 'error',
        summary: t('mistake'),
        detail: t('failed_to_load_company_data'),
        life: 3000
      });
    }
  };

  const getDepartmentData = (departmentId: number) => {
    HTTPService.GET(`/Department/ById?departmentId=${departmentId}`)
      .then((res) => {
        if (res.status === 200) {
          const data: IDepartmentData = res.data;
          setDepartmentData(data);
          form.setFieldsValue({
            departmentName: data.departmentName,
            companyId: data.companyId
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: res.error.message,
            life: 3000
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching department data:', error);
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('failed_to_load_department_data'),
          life: 3000
        });
      });
  };


  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };


  const updateDepartment = (formData: any) => {
    const updatedDepartment = {
      departmentId: departmentId,
      departmentName: formData.departmentName,
      companyId: formData.companyId || userInfo.companyId,
    };

    HTTPService.PUT(`/Department/updateDepartment`, updatedDepartment)
      .then((res) => {
        if (res.status === 200) {
          toast.current?.show({
            severity: 'success',
            summary: t('successful'),
            detail: t(res.data),
            life: 3000
          });
          window.location.replace("/admin/department-management");
        } else if (res.status === 450 || res.status === 400) {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t('update_failed'),
            life: 3000
          });
        }
      })
      .catch((error) => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('department_update_error'),
          life: 3000
        });
      });
  };

  const handleCancel = () => {
    window.location.replace("/admin/department-management/");
  };

  return (
    <>
      <Toast ref={toast} />
      <Form
        form={form}
        layout="vertical"
        onFinish={updateDepartment}
        fields={[
          { name: ["companyId"], value: departmentData?.companyId || userInfo.companyId },
          { name: ["departmentName"], value: departmentData?.departmentName },
        ]}
      >
        <Card style={{ padding: "50px" }}>
          <h3>{t('update_department')}</h3>
          {userInfo?.role === UserRole.AltisAdmin && (
            <Form.Item
              name='companyId'
              label={t('Select company')}
              rules={[{ required: true, message: 'Please select a company!' }]}
            >
              <Select
                showSearch
                placeholder={t('company_name')}
                style={{ marginBottom: '6px' }}
                filterOption={filterOption}
              >
                {companyData?.map((company) => (
                  <Select.Option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="departmentName"
                label={t('department_name')}
                rules={[{ required: true, message: "Please input your Department Name!", whitespace: true }]}
                style={{ marginBottom: 0 }} // Remove margin if needed
              >
                <Input
                  style={{ borderRadius: 0, width: '100%' }} // Set width to 100% for responsiveness
                  placeholder={t('department_name')}
                />
              </Form.Item>
            </Col>
          </Row>
          <br />
          <Form.Item>
            <Row gutter={[4, 4]} justify="end" align="middle">
              <Col xs={12} sm={4}>
                <Button
                  block
                  onClick={handleCancel}
                  type="primary"
                  style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                >
                  <CloseOutlined style={{ fontSize: 20 }} />
                  <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('cancel')}
                  </span>
                </Button>
              </Col>
              <Col xs={12} sm={4}>
                <Button
                  block
                  htmlType="submit"
                  type="primary"
                  style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                >
                  <SyncOutlined style={{ fontSize: 20 }} />
                  <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('update')}
                  </span>
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Card>
      </Form>
    </>
  );
};

export default UpdateDepartment;
